import { UUID_SEGMENT_REGEX } from "@/constants/regex";

/**
 * Resolve the org UUID of the customer whose page is currently being viewed.
 *
 * The customer Settings > Connections pages live under the scoped route
 * `/customer/:orgUuid(UUID)`, so `route.params.orgUuid` is the authoritative
 * identity of the customer being onboarded: it is validated as a UUID by the
 * router matcher and can never be the logged-in principal's (System/MSP) org
 * (those live under `/msp/:orgUuid` and `/system/:orgUuid`).
 *
 * This deliberately does NOT accept `selectedAccount` as a source. The old
 * `selectedCustomer?.uuid || selectedAccount?.uuid` fallback was the root cause
 * of the mis-onboarding bug: when a System/MSP admin onboarded a customer while
 * `selectedCustomer` happened to be empty, the customer's M365 tenant was bound
 * to the *principal's* org (System/MSP) instead of the customer's. Keeping the
 * principal's org out of this function's signature makes that failure mode
 * impossible by construction.
 *
 * Returns the first valid UUID from the route param, then `selectedCustomer`,
 * or `null` when neither is a valid UUID (callers must refuse to onboard rather
 * than fall back to anything else).
 */
export const resolveCustomerOrgUuid = (
  routeOrgUuid: unknown,
  selectedCustomerUuid?: string | null,
): string | null => {
  for (const candidate of [routeOrgUuid, selectedCustomerUuid]) {
    if (typeof candidate === "string" && UUID_SEGMENT_REGEX.test(candidate)) {
      return candidate;
    }
  }
  return null;
};

export default resolveCustomerOrgUuid;
